import React, { useEffect, useState } from 'react';
import {
  Badge,
  Button,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  FormGroup,
  FormHelperText,
  HelperText,
  HelperTextItem,
  MenuToggle,
  Select,
  SelectList,
  SelectOption,
  TextArea,
  TextInput,
} from '@patternfly/react-core';
import { SearchIcon } from '@patternfly/react-icons';

// Building blocks shared by the staff GPU tabs, the server detail page and their modals.

/** The trailing value of `value` after `delay` ms without change (the list-table search debounce). */
export function useDebouncedValue(value: string, delay: number = 400): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handle);
  }, [value, delay]);
  return debounced;
}

interface StatusMultiSelectProps {
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (next: string[]) => void;
  /** What the control does, e.g. "Filter by status" (never the current value). */
  ariaLabel: string;
  /** Visible toggle text. */
  label: string;
}

/** A checkbox select (PF `role="menu"` + `hasCheckbox`) with a count badge on its toggle. */
export const StatusMultiSelect: React.FunctionComponent<StatusMultiSelectProps> = ({
  options,
  selected,
  onChange,
  ariaLabel,
  label,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const onSelect = (_event: React.MouseEvent | undefined, value: string | number | undefined) => {
    if (typeof value !== 'string') {
      return;
    }
    onChange(selected.includes(value) ? selected.filter((s) => s !== value) : [...selected, value]);
  };
  return (
    <Select
      role="menu"
      isOpen={isOpen}
      selected={selected}
      onSelect={onSelect}
      onOpenChange={(open) => setIsOpen(open)}
      toggle={(toggleRef) => (
        <MenuToggle
          ref={toggleRef}
          onClick={() => setIsOpen(!isOpen)}
          isExpanded={isOpen}
          aria-label={ariaLabel}
          badge={selected.length > 0 ? <Badge isRead>{selected.length}</Badge> : undefined}
        >
          {label}
        </MenuToggle>
      )}
    >
      <SelectList>
        {options.map((option) => (
          <SelectOption
            key={option.value}
            value={option.value}
            hasCheckbox
            isSelected={selected.includes(option.value)}
          >
            {option.label}
          </SelectOption>
        ))}
      </SelectList>
    </Select>
  );
};

/** "No … match your search" — distinct from the empty-list state. */
export const NoSearchResults: React.FunctionComponent<{ what: string; onClear: () => void }> = ({ what, onClear }) => (
  <EmptyState titleText={`No ${what} match your search`} icon={SearchIcon} variant="lg" headingLevel="h2">
    <EmptyStateBody>Try a different search term, or clear the filter to see all {what}.</EmptyStateBody>
    <EmptyStateFooter>
      <EmptyStateActions>
        <Button variant="primary" onClick={onClear}>
          Clear filter
        </Button>
      </EmptyStateActions>
    </EmptyStateFooter>
  </EmptyState>
);

/** Case- and whitespace-insensitive match for a typed confirmation. */
export function typedMatches(value: string, expected: string): boolean {
  return value.trim().toLowerCase() === expected.trim().toLowerCase();
}

interface TypedConfirmFieldProps {
  id: string;
  /** The text to type out (a hostname, a search marker). */
  expected: string;
  value: string;
  onChange: (value: string) => void;
  /** Helper shown before anything is typed. */
  hint: string;
}

/** "Type <expected> to confirm", with live validation feedback. */
export const TypedConfirmField: React.FunctionComponent<TypedConfirmFieldProps> = ({
  id,
  expected,
  value,
  onChange,
  hint,
}) => {
  const touched = value.length > 0;
  const matches = typedMatches(value, expected);
  const state = !touched ? 'default' : matches ? 'success' : 'error';
  return (
    <FormGroup label={`Type ${expected} to confirm`} fieldId={id} isRequired>
      <TextInput
        id={id}
        name={id}
        type="text"
        value={value}
        onChange={(_event, next) => onChange(next)}
        validated={state}
        isRequired
        autoComplete="off"
        aria-describedby={`${id}-helper`}
      />
      <FormHelperText>
        <HelperText isLiveRegion>
          <HelperTextItem id={`${id}-helper`} variant={state}>
            {!touched ? hint : matches ? 'It matches.' : `That is not ${expected}.`}
          </HelperTextItem>
        </HelperText>
      </FormHelperText>
    </FormGroup>
  );
};

/** The backend's note bound: 1–255 characters (counted as characters, accents are fine). */
export const NOTE_MAX_LENGTH = 255;

/** `true` when a note is 1–255 characters once trimmed. */
export function isValidNote(note: string): boolean {
  const length = Array.from(note.trim()).length;
  return length >= 1 && length <= NOTE_MAX_LENGTH;
}

interface NoteFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** The backend's `data.note` message, if any. */
  error: string | null;
  hint: string;
}

/** A required 1–255 character note (what was decided, for the audit trail). */
export const NoteField: React.FunctionComponent<NoteFieldProps> = ({ id, label, value, onChange, error, hint }) => {
  const length = Array.from(value.trim()).length;
  const tooLong = length > NOTE_MAX_LENGTH;
  const message = error ?? (tooLong ? `At most ${NOTE_MAX_LENGTH} characters (${length} now).` : null);
  return (
    <FormGroup label={label} fieldId={id} isRequired>
      <TextArea
        id={id}
        name={id}
        value={value}
        onChange={(_event, next) => onChange(next)}
        isRequired
        validated={message !== null ? 'error' : 'default'}
        resizeOrientation="vertical"
        aria-describedby={`${id}-helper`}
      />
      <FormHelperText>
        <HelperText>
          <HelperTextItem id={`${id}-helper`} variant={message !== null ? 'error' : 'default'}>
            {message ?? hint}
          </HelperTextItem>
        </HelperText>
      </FormHelperText>
    </FormGroup>
  );
};
